import { Fragment, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { formatCurrency, formatNumber } from "@/utils/format";
import type { OutcomeItem } from "@/services/financeiro";
import { TotalInvestidoPlanoMae } from "./chartUtils";

interface Props {
  open:          boolean;
  onClose:       () => void;
  hierarchy:     TotalInvestidoPlanoMae[];
  grandTotal:    number;
  periodLabel:   string;
  contextNote?:  string;
  onOpenParcelas: (items: OutcomeItem[], title: string) => void;
}

export function TotalInvestidoPlanoModal({
  open,
  onClose,
  hierarchy,
  grandTotal,
  periodLabel,
  contextNote,
  onOpenParcelas,
}: Props) {

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!open) return;
    setExpanded(new Set());
  }, [open, hierarchy]);

  const toggle = (prefix: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(prefix)) next.delete(prefix);
      else next.add(prefix);
      return next;
    });
  };

  const totalParcelas = useMemo(
    () => hierarchy.reduce((s, m) => s + m.parcelCount, 0),
    [hierarchy],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Total investido por plano de contas"
      size="lg"
    >
      <p className="text-sm text-slate-500 mb-4">
        Escopo: categorias cujo código Sienge começa em{" "}
        <strong>210</strong>, <strong>211</strong>, <strong>207</strong>, <strong>201</strong> ou{" "}
        <strong>202</strong>. OBRA e ADM Obra usam os grupos da planilha financeira; tributárias e demais capítulos
        abrem direto os pagamentos ao clicar na linha.
        {periodLabel ? ` ${periodLabel}` : ""}
        {contextNote ? ` ${contextNote}` : ""}
      </p>
      <p className="text-xs text-slate-400 -mt-2 mb-3">
        Expanda <strong>OBRA</strong> ou <strong>Despesas administrativas obra</strong> para ver os grupos fixos.
        Clique em um grupo (ou na linha dos capítulos 207 / 201 / 202) para abrir todos os pagamentos agregados —
        mesmo formato do Financeiro.
      </p>

      {hierarchy.length === 0 ? (
        <p className="text-sm text-slate-600 py-6 text-center">
          Nenhuma despesa nos planos 210, 211, 207, 201 ou 202 para o período e filtros aplicados.
        </p>
      ) : (
        <div className="rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <th className="px-2 py-3 w-10" aria-hidden />
                <th className="px-3 py-3 whitespace-nowrap w-[100px]">Código</th>
                <th className="px-3 py-3">Plano de contas</th>
                <th className="px-3 py-3 text-right whitespace-nowrap">Valor</th>
                <th className="px-3 py-3 text-right whitespace-nowrap">% do total</th>
                <th className="px-3 py-3 text-right whitespace-nowrap">Parcelas</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {hierarchy.map((mae) => {
                const isExp = expanded.has(mae.prefix);
                const expandable = mae.expandable;

                return (
                  <Fragment key={mae.prefix}>
                    <tr
                      className={`bg-slate-100/95 transition-colors ${
                        expandable
                          ? "hover:bg-slate-100 cursor-pointer"
                          : "hover:bg-rose-50/50 cursor-pointer"
                      }`}
                      onClick={() => {
                        if (expandable) toggle(mae.prefix);
                        else if (mae.directItems.length > 0) {
                          onOpenParcelas(mae.directItems, mae.title);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter" && e.key !== " ") return;
                        e.preventDefault();
                        if (expandable) toggle(mae.prefix);
                        else if (mae.directItems.length > 0) {
                          onOpenParcelas(mae.directItems, mae.title);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      title={
                        expandable
                          ? "Expandir ou recolher grupos"
                          : "Ver todos os pagamentos deste capítulo"
                      }
                    >
                      <td className="px-2 py-2.5 text-slate-500 align-middle">
                        {expandable ? (
                          isExp ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )
                        ) : (
                          <span className="inline-block w-4" aria-hidden />
                        )}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs font-semibold text-slate-800 tabular-nums whitespace-nowrap align-middle">
                        {mae.prefix}
                      </td>
                      <td className="px-3 py-2.5 text-slate-900 font-semibold leading-snug align-middle">
                        {mae.title}
                        {!expandable && (
                          <span className="block text-[11px] font-normal text-slate-400 mt-0.5">
                            Clique para ver pagamentos
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-bold text-slate-900 whitespace-nowrap align-middle">
                        {formatCurrency(mae.total)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap align-middle">
                        {mae.pctOfGrand.toFixed(1)}%
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap align-middle">
                        {formatNumber(mae.parcelCount)}
                      </td>
                    </tr>
                    {expandable &&
                      isExp &&
                      mae.buckets.map((bucket) => (
                        <tr
                          key={`${mae.prefix}-${bucket.key}`}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenParcelas(bucket.items, bucket.title);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              onOpenParcelas(bucket.items, bucket.title);
                            }
                          }}
                          className="cursor-pointer hover:bg-rose-50/60 bg-white"
                        >
                          <td className="px-2 py-2" aria-hidden />
                          <td className="px-3 py-2 pl-4 font-mono text-xs text-slate-400 tabular-nums whitespace-nowrap align-top border-l-2 border-slate-200">
                            —
                          </td>
                          <td className="px-3 py-2 text-slate-800 font-medium leading-snug">
                            {bucket.title}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-800 whitespace-nowrap">
                            {formatCurrency(bucket.total)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-600 whitespace-nowrap">
                            {bucket.pctOfGrand.toFixed(1)}%
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-600 whitespace-nowrap">
                            {formatNumber(bucket.parcelCount)}
                          </td>
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-rose-50/80 border-t border-rose-100">
                <td colSpan={3} className="px-3 py-3 font-semibold text-slate-800">
                  Total
                </td>
                <td className="px-3 py-3 text-right font-bold text-rose-700 tabular-nums whitespace-nowrap">
                  {formatCurrency(grandTotal)}
                </td>
                <td className="px-3 py-3 text-right text-slate-500 whitespace-nowrap">100%</td>
                <td className="px-3 py-3 text-right tabular-nums text-slate-700 whitespace-nowrap">
                  {formatNumber(totalParcelas)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Modal>
  );
}
